/**
 * A string replace on shader source that complains when it matches nothing.
 *
 * Every custom material here works by `.replace()`-ing three's own GLSL, and a
 * `.replace()` whose needle is absent is a no-op that returns the original
 * string. No exception, no warning, a clean compile, a rendered frame — and the
 * feature simply is not there.
 *
 * That has now happened twice. The snow subsurface term silently did nothing
 * because three's source carries a blank line between
 *
 *     float dotNL = saturate( dot( geometryNormal, directLight.direction ) );
 *     vec3 irradiance = dotNL * directLight.color;
 *
 * and the needle did not. Nothing about the build, the screenshots, the console
 * or the GL error state distinguished "applied" from "quietly skipped".
 *
 * Prefer a RegExp needle for anything spanning more than one line. The first
 * two attempts at the snow term failed purely on whitespace — three's source
 * indents with tabs and carries a blank line, and matching that exactly through
 * an editor and a build step is a coin flip. \s+ does not care.
 *
 * These are chunk names and vendor source, so they break on a three upgrade
 * rather than on anything we write — which is exactly when a silent no-op is
 * least likely to be noticed. Failing loudly costs one comparison per compile.
 * It is reported rather than thrown because a missing flourish should not take
 * the game down, and because the screenshot harness now listens to
 * console.error, so a miss fails the tests without failing the player.
 */
export function mustReplace(
  source: string,
  find: string | RegExp,
  replacement: string,
  label: string,
): string {
  const hit = typeof find === 'string' ? source.includes(find) : find.test(source)
  if (!hit) {
    console.error(
      `[fall line] shader patch "${label}" matched nothing — the effect is NOT ` +
      'applied. three\'s shader source has probably changed under it.',
    )
    return source
  }
  return source.replace(find, replacement)
}
